import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BRAND } from "@/data/brand";

/**
 * Cinematic loading sequence — constructs the AG monogram with concentric rings,
 * then dissolves into the page. Shows once per session.
 */
export default function LoadingExperience() {
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    return !sessionStorage.getItem("ag_loaded");
  });

  useEffect(() => {
    if (!show) return;
    const timer = setTimeout(() => {
      sessionStorage.setItem("ag_loaded", "1");
      setShow(false);
    }, 2400);
    return () => clearTimeout(timer);
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-[100] bg-midnight flex items-center justify-center overflow-hidden"
          data-testid="loading-experience"
        >
          {/* Background gradient that fades in */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2 }}
            className="absolute inset-0 bg-midnight-gradient"
          />

          {/* Ambient blue glow */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1.2 }}
            transition={{ duration: 1.6, ease: "easeOut" }}
            className="absolute"
            style={{
              width: 800,
              height: 800,
              background: "radial-gradient(circle, rgba(0,119,255,0.35) 0%, rgba(0,119,255,0.1) 30%, transparent 70%)",
              filter: "blur(60px)",
            }}
          />

          {/* Concentric rings drawing outward */}
          <div className="relative" style={{ width: 480, height: 480 }}>
            {[1, 0.82, 0.62, 0.45].map((scale, i) => (
              <motion.div
                key={`ring-${scale}`}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale, opacity: i === 3 ? 0 : 1 }}
                transition={{
                  duration: 1.4,
                  delay: 0.15 + i * 0.12,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="absolute inset-0 rounded-full border"
                style={{
                  borderColor: i === 0 ? "rgba(198,168,107,0.5)" : i === 1 ? "rgba(0,170,255,0.4)" : "rgba(255,255,255,0.15)",
                  borderWidth: 1,
                }}
              />
            ))}

            {/* Rotating outer accent ring */}
            <motion.div
              initial={{ opacity: 0, rotate: 0 }}
              animate={{ opacity: 1, rotate: 180 }}
              transition={{ duration: 2, ease: "easeOut" }}
              className="absolute inset-0 rounded-full"
              style={{
                background: "conic-gradient(from 0deg, transparent 0deg, rgba(198,168,107,0.6) 90deg, transparent 180deg, rgba(0,170,255,0.5) 270deg, transparent 360deg)",
                maskImage: "radial-gradient(circle, transparent 49%, black 50%, black 51%, transparent 52%)",
                WebkitMaskImage: "radial-gradient(circle, transparent 49%, black 50%, black 51%, transparent 52%)",
              }}
            />

            {/* Monogram fades in */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <div className="relative flex items-center justify-center" style={{ width: 240, height: 240 }}>
                <img
                  src={BRAND.logoWhite}
                  alt=""
                  className="max-w-full max-h-full object-contain"
                  style={{ filter: "drop-shadow(0 0 30px rgba(198,168,107,0.4))" }}
                  draggable={false}
                />
                {/* Metallic shimmer overlay */}
                <motion.div
                  initial={{ x: "-100%", opacity: 0 }}
                  animate={{ x: "100%", opacity: [0, 0.5, 0] }}
                  transition={{ duration: 1.5, delay: 1.2, ease: "easeInOut" }}
                  className="absolute inset-0"
                  style={{
                    background: "linear-gradient(120deg, transparent 35%, rgba(255,255,255,0.45) 50%, transparent 65%)",
                    mixBlendMode: "screen",
                  }}
                />
              </div>
            </motion.div>
          </div>

          {/* Brand label appears below — just the license microcopy since logo already has wordmark */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="absolute bottom-24 left-1/2 -translate-x-1/2 text-center"
          >
            <div className="font-mono text-[0.6rem] text-gold tracking-[0.4em] uppercase">Licensed · FL</div>
          </motion.div>

          {/* Bottom mono ticker */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.6 }}
            className="absolute bottom-8 left-8 font-mono text-[0.6rem] text-ag-text-muted tracking-[0.3em] uppercase"
          >
            <div className="flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-glow-soft animate-pulse" />
              Initializing
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.6 }}
            className="absolute bottom-8 right-8 font-mono text-[0.6rem] text-ag-text-muted tracking-[0.3em] uppercase"
          >
            EST · FLORIDA
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
